import useDateLocale from '@/common/hooks/useDateLocale'
import {
  Alert,
  AlertIcon,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  UseModalProps,
  VStack,
} from '@chakra-ui/react'
import { LogFragment } from '@gql'
import {
  EntitiesChanges,
  EntityChange,
  LogType,
} from '@rolebase/shared/model/log'
import { capitalizeFirstLetter } from '@utils/capitalizeFirstLetter'
import { format } from 'date-fns'
import React from 'react'
import { useTranslation } from 'react-i18next'
import LogEntityChanges from '../components/LogEntityChanges'
import LogText from '../components/LogText'

interface Props extends UseModalProps {
  log: LogFragment
}

const headingKeys = {
  [LogType.RoleUpdate]: 'LogDetailsModal.heading_update',
  [LogType.RoleCreate]: 'LogDetailsModal.heading_create',
  [LogType.CircleCreate]: 'LogDetailsModal.heading_create',
  [LogType.CircleMove]: 'LogDetailsModal.heading_move',
} as const

/**
 * What a change to the org chart did, from the news feed (#114): read only.
 * Undoing a governance change stays where it is today, never a click away
 * in a feed one scrolls through (D4).
 */
export default function LogDetailsModal({ log, ...modalProps }: Props) {
  const { t } = useTranslation()
  const dateLocale = useDateLocale()
  const headingKey =
    headingKeys[log.display.type as keyof typeof headingKeys] ??
    'LogDetailsModal.heading_update'

  return (
    <Modal size="xl" {...modalProps}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t(headingKey, { name: log.display.name })}</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Text>
            <LogText log={log} />
          </Text>
          <Text fontSize="sm" color="gray.500" mb={4}>
            {capitalizeFirstLetter(
              format(new Date(log.createdAt), 'PPPp', { locale: dateLocale })
            )}
          </Text>

          {log.canceled && (
            <Alert status="warning" mb={4}>
              <AlertIcon />
              {t('LogDetailsModal.canceled')}
            </Alert>
          )}

          <VStack spacing={5} align="stretch" wordBreak="break-word">
            {Object.keys(log.changes).flatMap(
              (entityType) =>
                log.changes[entityType as keyof EntitiesChanges]?.map(
                  (entityChange, index) => (
                    <LogEntityChanges
                      key={`${entityType}-${index}`}
                      type={entityType}
                      entityChange={entityChange as EntityChange<any>}
                    />
                  )
                ) ?? []
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button onClick={modalProps.onClose}>{t('common.close')}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
